import {
  createReducer,
  isAnyOf,
} from '@reduxjs/toolkit'

import {
  FeatureKey,
  getFeatureKeysObject,
} from '@/constants/feature-key'
import { Employee } from '@/model/employee'
import {
  activateEmployee,
  createEmployee,
  deactivateEmployee,
  employeeNamespace,
  readAllEmployees,
  readMineEmployee,
  removeEmployee,
  updateEmployee,
  updateMineEmployee,
} from '@/redux/employee-actions'
import {
  removeItem,
  updateOrAddArrayItem,
} from '@/utils/draft'
import { getTimezoneName } from '@/utils/timezone'

const initialState: {
  currentEmployee?: Employee
  employees: Record<FeatureKey, Employee[]>
  employeesFetching: boolean
} = {
  currentEmployee: undefined,
  employees: getFeatureKeysObject([]),
  employeesFetching: false,
}

const mapTimezone = (employee: Employee) => {
  const { regularTimezone, reportTimezone } = employee
  return {
    ...employee,
    reportTimezoneName: getTimezoneName(reportTimezone),
    regularTimezoneName: getTimezoneName(regularTimezone),
  }
}

const reducer = createReducer(initialState, (builder) => {
  builder.addCase(readAllEmployees.pending, (draft) => {
    draft.employeesFetching = false
  })
  builder.addCase(readAllEmployees.rejected, (draft) => {
    draft.employeesFetching = false
  })
  builder.addCase(readAllEmployees.fulfilled, (draft, { payload: { data, permissionKey } }) => {
    draft.employees[permissionKey] = data.rows.map(mapTimezone)
    draft.employeesFetching = false
  })
  builder.addCase(createEmployee.fulfilled, (draft, { payload: { data, permissionKey } }) => {
    draft.employees[permissionKey].push(mapTimezone(data))
  })
  builder.addCase(removeEmployee.fulfilled, (draft, { meta }) => {
    Object.values(draft.employees).forEach((items) => removeItem(meta.arg, items))
  })

  builder.addMatcher(isAnyOf(
    readMineEmployee.fulfilled,
    updateMineEmployee.fulfilled,
  ), (draft, { payload }) => {
    draft.currentEmployee = mapTimezone(payload)
    Object.values(draft.employees).forEach((items) => updateOrAddArrayItem(
      payload,
      items.map(mapTimezone),
    ))
  })

  builder.addMatcher(isAnyOf(
    updateEmployee.fulfilled,
    activateEmployee.fulfilled,
    deactivateEmployee.fulfilled,
  ), (draft, { payload }) => {
    Object.values(draft.employees).forEach((items) => updateOrAddArrayItem(
      payload,
      items.map(mapTimezone),
    ))
  })
})

export default { [employeeNamespace]: reducer }
